/**
 * PremiumModal — iter-104 — single-offer upgrade sheet.
 *
 * Collapsed from two tiers (Plus + Keeper) into ONE: MoodCard Plus.
 * Monthly $7.99 / annual $59.99 (~3 months free framing).
 * Premium is a STATE, not a destination — surfaced contextually from
 * locked tiles, exhaust screens, and the pour-limit CTA.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Eraser, Layers, Check, Wand2, Star, PenTool, Heart } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";
import AuthButton from "./AuthButton";

const FEATURES = [
    { icon: Layers,   text: "All Style Packs — 16 looks · luxury · scrapbook · handwritten" },
    { icon: Eraser,   text: "No watermark on any export" },
    { icon: Wand2,    text: "Motion + animated MP4 / WebM exports" },
    { icon: PenTool,  text: "Emphasis Mode — mixed fonts & accents within a card" },
    { icon: Sparkles, text: "Unlimited cards · priority generation · brand colors" },
];

const MONTHLY_PRICE = 7.99;
const ANNUAL_PRICE  = 59.99;

export default function PremiumModal({ open, onOpenChange, source = "generic" }) {
    const { user, isPremium } = useAuth();
    const [busy, setBusy] = useState(false);
    const [billing, setBilling] = useState("month");

    useEffect(() => {
        if (open) track("premium_modal_open", { source });
    }, [open, source]);

    const checkout = async () => {
        const productId = billing === "year" ? "plus_annual" : "plus";
        setBusy(true);
        try {
            track("checkout_start", { product_id: productId, source });
            const { data } = await axios.post(
                `${API}/checkout/premium`,
                { product_id: productId, origin_url: window.location.origin },
                { withCredentials: true }
            );
            if (data.url) window.location.href = data.url;
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 401 && detail?.error === "auth_required") {
                track("checkout_auth_required", { product_id: productId });
                try {
                    window.sessionStorage.setItem("moodcard.pending_checkout", JSON.stringify({
                        product_id: productId, ts: Date.now(),
                    }));
                } catch { /* ignore */ }
                window.dispatchEvent(new CustomEvent("open-auth-modal", {
                    detail: { source: "checkout", resume_checkout: productId },
                }));
                onOpenChange?.(false);
            }
            setBusy(false);
        }
    };

    const headlinePrice = billing === "year" ? `$${ANNUAL_PRICE}/yr` : `$${MONTHLY_PRICE}/mo`;
    const perMonthIfAnnual = (ANNUAL_PRICE / 12).toFixed(2);
    const annualSavings = (MONTHLY_PRICE * 12 - ANNUAL_PRICE).toFixed(2);
    const ctaLabel = billing === "year" ? `Start — $${ANNUAL_PRICE}/yr` : `Start — $${MONTHLY_PRICE}/mo`;

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }} onClick={() => onOpenChange(false)}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
                    <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none overflow-y-auto"
                        data-testid="premium-modal">
                        <div className="relative w-full max-w-md my-auto rounded-3xl border border-app/30 bg-surface-1 backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,0.7)] overflow-hidden pointer-events-auto">
                            <div className="absolute inset-0 grain pointer-events-none" />
                            <div className="relative p-6">
                                <div className="flex items-start justify-between mb-5">
                                    <div>
                                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">MoodCard Plus</span>
                                        <h3 className="mt-2 font-serif font-light italic text-app text-3xl tracking-tight">
                                            {isPremium ? "you're in." : "say it beautifully."}
                                        </h3>
                                    </div>
                                    <button onClick={() => onOpenChange(false)} data-testid="premium-modal-close"
                                        className="p-2 rounded-full text-app-3 hover:text-app hover:bg-surface-2 transition-colors">
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>

                                {isPremium ? (
                                    <div className="text-center py-6">
                                        <div className="mx-auto h-12 w-12 rounded-full border border-app/40 bg-surface-2 flex items-center justify-center mb-4">
                                            <Check className="w-5 h-5 text-app" strokeWidth={2} />
                                        </div>
                                        <p className="font-serif italic text-app text-lg mb-2">all premium features unlocked.</p>
                                        <p className="font-sans text-sm text-app-3">Every Style Pack, animation, and export size — yours.</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Billing toggle */}
                                        <div className="flex flex-col items-center gap-2 mb-5">
                                            <div className="flex items-center justify-center gap-1 p-1 rounded-full border border-app/20 bg-surface-2" data-testid="billing-toggle">
                                                {[
                                                    { k: "month", l: "Monthly" },
                                                    { k: "year",  l: "Annual · 3 months free" },
                                                ].map(({ k, l }) => (
                                                    <button key={k} onClick={() => setBilling(k)}
                                                        data-testid={`billing-${k}`}
                                                        className={`px-4 py-1.5 rounded-full font-sans text-[11px] uppercase tracking-[0.18em] transition-colors ${
                                                            billing === k ? "bg-cta text-app-inverse" : "text-app-2 hover:text-app"
                                                        }`}>
                                                        {l}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="font-serif italic text-[11px] text-app-3">
                                                {billing === "month"
                                                    ? `Switch to annual — works out to $${perMonthIfAnnual}/mo.`
                                                    : `You're saving $${annualSavings} a year · about 3 months free.`}
                                            </p>
                                        </div>

                                        {/* === SINGLE OFFER CARD === */}
                                        <div
                                            data-testid="plan-card-plus"
                                            className="relative rounded-2xl p-[1.5px] mb-4 metal-bezel"
                                            style={{
                                                boxShadow: "0 0 40px -8px rgba(107,214,200,0.22), 0 22px 50px -20px rgba(0,0,0,0.6)",
                                            }}
                                        >
                                            <div className="relative rounded-2xl metal-surface px-5 pt-7 pb-5">
                                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                    <div className="px-3 py-1 rounded-full bg-cta text-app-inverse font-sans text-[10px] uppercase tracking-[0.3em] font-medium shadow-md flex items-center gap-1.5">
                                                        <Heart className="w-3 h-3" strokeWidth={2} fill="currentColor" />
                                                        Everything · One Plan
                                                    </div>
                                                </div>

                                                <div className="flex items-baseline justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-sans text-xs uppercase tracking-[0.3em] text-app font-medium">Plus</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-serif text-app text-2xl tracking-tight" data-testid="plus-price">{headlinePrice}</div>
                                                        <div className="font-sans text-[10px] text-app-3">
                                                            {billing === "year"
                                                                ? `just $${perMonthIfAnnual}/mo · billed yearly`
                                                                : "billed monthly"}
                                                        </div>
                                                    </div>
                                                </div>

                                                <ul className="mt-4 mb-4 space-y-2">
                                                    {FEATURES.map(({ icon: Icon, text }) => (
                                                        <li key={text} className="flex items-start gap-2.5">
                                                            <div className="h-5 w-5 shrink-0 mt-0.5 rounded-full bg-cta/15 border border-app/20 flex items-center justify-center">
                                                                <Icon className="w-3 h-3 text-app" strokeWidth={1.8} />
                                                            </div>
                                                            <span className="font-sans text-sm text-app leading-snug">{text}</span>
                                                        </li>
                                                    ))}
                                                </ul>

                                                <button
                                                    onClick={checkout}
                                                    disabled={busy}
                                                    data-testid="plus-checkout-button"
                                                    className="btn-metal w-full py-4 rounded-full font-sans font-medium text-sm tracking-wide disabled:opacity-50"
                                                >
                                                    <span className="relative z-10">{busy ? "Redirecting…" : ctaLabel}</span>
                                                </button>
                                                <p className="text-center font-sans text-[10px] text-app-3 mt-2">
                                                    Cancel anytime · works on web + mobile
                                                </p>
                                            </div>
                                        </div>

                                        {!user && (
                                            <div className="mt-5 pt-5 border-t border-app/20">
                                                <p className="text-center font-sans text-[11px] text-app-3 mb-3">
                                                    Sign in to keep your tier across devices.
                                                </p>
                                                <div className="flex justify-center">
                                                    <AuthButton />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
